/** @format */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActiveState, EntityState, EntityStore, QueryEntity, StoreConfig } from '@datorama/akita';
import { OtmId, UserSettings } from 'common-typescript/types';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AuthService, ServiceDowngradeMappings } from 'sis-common';

@Injectable({ providedIn: 'root' })
export class UserSettingsEntityService {
    static readonly downgrade: ServiceDowngradeMappings = {
        dependencies: [],
        moduleName: 'sis-components.service.userSettingsEntityService',
        serviceName: 'userSettingsEntityService',
    };

    private readonly api = '/ori/api/user-settings';
    private readonly store = new UserSettingsStore();
    private readonly query = new UserSettingsQuery(this.store);

    constructor(
        private readonly httpClient: HttpClient,
        private readonly authService: AuthService,
    ) {}

    getOwnSettings(bypassCache = false): Observable<UserSettings> {
        if (!this.authService.loggedIn()) {
            return of(null);
        }

        if (!bypassCache && this.query.hasActive()) {
            return this.query.selectActive();
        }
        return this.httpClient.get<UserSettings>(`${this.api}/own`).pipe(
            switchMap(settings => {
                this.store.upsert(settings.personId, settings);
                this.store.setActive(settings.personId);
                return this.query.selectActive();
            }),
        );
    }

    saveOwnSettings(settings: Partial<UserSettings>): Observable<UserSettings> {
        if (!this.authService.loggedIn()) {
            return of(null);
        }

        return this.httpClient.post<UserSettings>(`${this.api}/own`, settings).pipe(
            switchMap(savedSettings => {
                this.store.upsert(savedSettings.personId, savedSettings);
                this.store.setActive(savedSettings.personId);
                return this.query.selectActive();
            }),
        );
    }
}

type UserSettingsState = EntityState<UserSettings, OtmId> & ActiveState;

@StoreConfig({
    name: 'user-settings',
    idKey: 'personId',
})
class UserSettingsStore extends EntityStore<UserSettingsState> {}

class UserSettingsQuery extends QueryEntity<UserSettingsState> {
    constructor(protected store: UserSettingsStore) {
        super(store);
    }
}
